import { Block } from 'models'
import { Fragment, ReactNode } from 'react'

export const processInline = (block: string) => {
  let parts: ReactNode[] = []

  const linkPattern = /(\[[^|]+\|[^\]]+\])/gu
  const linkPartsPattern = /\[([^|]+)\|([^\]]+)\]/u

  const boldPattern = /(\*\*[^*]+\*\*)/gu
  const boldPartsPattern = /(\*\*([^*]+)\*\*)/u

  const italicPattern = /(__[^_]+__)/gu
  const italicPartsPattern = /(__([^_]+)__)/u

  parts = block.split(linkPattern)
  parts = parts.map(p => (p as string).split(boldPattern)).flat()
  parts = parts.map(p => (p as string).split(italicPattern)).flat()

  parts = parts.map(part => {
    if (linkPartsPattern.test(part as string)) {
      const tokens = (part as string).match(linkPartsPattern) as [
        string,
        string,
        string
      ]
      return (
        <a href={tokens[2]} target="_blank" rel="noreferrer">
          {tokens[1]}
        </a>
      )
    } else if (boldPartsPattern.test(part as string)) {
      const tokens = (part as string).match(boldPartsPattern) as [
        string,
        string,
        string
      ]
      return <strong>{tokens[2]}</strong>
    } else if (italicPartsPattern.test(part as string)) {
      const tokens = (part as string).match(italicPartsPattern) as [
        string,
        string,
        string
      ]
      return <em>{tokens[2]}</em>
    }
    return part
  })

  return parts.length ? (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </>
  ) : (
    block
  )
}

// Removes GDB url params
const cleanImageUrl = (url: string) => {
  const matches = url.match(
    /(https:\/\/gdb\.rferl\.org\/[a-z0-9-]+)[_a-z0-9]*(\.[a-zA-Z]+)/u
  )
  return Array.isArray(matches) && matches.length === 3
    ? `${matches[1]}${matches[2]}`
    : url
}

const processBlock = (part: string): Block => {
  const cleanPart = part.trim()

  if (/^P:/iu.test(cleanPart)) {
    return {
      type: 'p',
      content: processInline(cleanPart.replace(/^P:/i, '')),
    }
  }

  if (/^QUOTE_NAME:/iu.test(cleanPart)) {
    return {
      type: 'person',
      name: cleanPart.replace(/^QUOTE_NAME:/i, '').trim(),
    }
  }

  if (/^TITLE:/iu.test(cleanPart)) {
    return {
      type: 'title',
      content: processInline(cleanPart.replace(/^TITLE:/i, '')),
    }
  }

  if (/^IMAGE:([^:]+):https?:/iu.test(cleanPart)) {
    const [_, description, ...rawImgUrl] = cleanPart.split(':')
    return {
      type: 'figure',
      src: cleanImageUrl(rawImgUrl.join(':')),
      description,
    }
  }

  if (/^IMAGE:/iu.test(cleanPart)) {
    // Removes GDB url params
    const rawImgUrl = cleanPart.replace(/^IMAGE:/i, '')
    return {
      type: 'figure',
      src: cleanImageUrl(rawImgUrl),
    }
  }

  if (/^VIDEO:/iu.test(cleanPart)) {
    return {
      type: 'video',
      src: cleanPart.replace(/^VIDEO:/i, ''),
    }
  }

  if (/^#+/u.test(cleanPart)) {
    const matches = cleanPart.match(/^#+/u)

    if (matches) {
      const HeadingEl = `h${matches[0].length + 3}` as 'h3' | 'h4' | 'h5' | 'h6'
      return {
        type: 'heading',
        element: HeadingEl,
        content: processInline(cleanPart.replace(/^#+/, '')),
      }
    }

    return {
      type: 'title',
      content: processInline(cleanPart.replace(/^#+/, '')),
    }
  }

  if (/^IFRAME:/iu.test(cleanPart)) {
    return {
      type: 'iframe',
      src: cleanPart.replace(/^IFRAME:/i, ''),
    }
  }

  return {
    type: 'p',
    content: processInline(cleanPart),
  }
}

export const parseMarkdown = (text: string) => {
  const parts = text.split(/\n+/)

  return parts.map(part => processBlock(part))
}

export const sanitizeImageUrl = (url: string | undefined, width: number) => {
  if (!url) {
    return url
  }

  const matchReg = /(https:\/\/gdb\.rferl\.org\/[a-z0-9-]+).*(\.jpe?g)/u
  // console.log(matchReg.test(url), url)

  if (matchReg.test(url)) {
    const matches = url.match(matchReg)

    if (matches?.length === 3) {
      return `${matches[1]}_w${width}${matches[2]}`
    }
  }

  return url || ''
}
